import React, { useState } from 'react';
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Building2,
  Ship,
  Globe,
  Phone,
  Mail,
  MapPin,
  Clock,
  RotateCw,
  Eye,
  CheckCircle2,
  Check,
  Smartphone,
  Monitor,
  Tablet,
  X,
  Layers,
  Palette,
  Type,
  FileText,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { IndustryType, AIDesignVibe, AISitePlan, WebsiteDesignConfig } from '../../types';
import { AIBusinessProfile } from '../../types/ai';
import { aiClientService } from '../../services/aiService';
import { SiteStructureService } from '../../services/siteStructureService';
import confetti from 'canvas-confetti';

interface AISiteCreationFlowProps {
  isOpen: boolean;
  initialIndustry?: IndustryType;
  onClose: () => void;
  onSiteCreated: (siteId: string) => void;
}

export const AISiteCreationFlow: React.FC<AISiteCreationFlowProps> = ({
  isOpen,
  initialIndustry,
  onClose,
  onSiteCreated,
}) => {
  const { createSiteWithAI, createNewSite, sectorQuotas, canCreateSectorWebsite } = useSite();
  const { currentUser } = useAuth();

  // Wizard Steps 1 to 7 (Step 8 is opening the visual editor)
  const [currentStep, setCurrentStep] = useState<number>(1);

  // STEP 1: Site Name & Subdomain
  const [siteName, setSiteName] = useState('');
  const [subdomain, setSubdomain] = useState('');

  // STEP 2: Industry
  const [industry, setIndustry] = useState<IndustryType>(initialIndustry || 'CORPORATE');

  React.useEffect(() => {
    if (initialIndustry) {
      setIndustry(initialIndustry);
      if (initialIndustry === 'YACHT_RENTAL') {
        setDesignVibe('AEGEAN_COASTAL');
      } else {
        setDesignVibe('CORPORATE_PRESTIGE');
      }
    }
  }, [initialIndustry]);

  // STEP 3: Natural Language Prompt & Tone of Voice
  const [userPrompt, setUserPrompt] = useState('');
  const [designVibe, setDesignVibe] = useState<AIDesignVibe>('CORPORATE_PRESTIGE');
  const [selectedHighlights, setSelectedHighlights] = useState<string[]>([
    'WhatsApp Doğrudan Rezervasyon Butonu',
    'Mobil Uyumlu Hızlı Tasarım',
  ]);

  // STEP 4: Real Business Data
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState(currentUser?.phone || '+90 (532) 000 00 00');
  const [companyEmail, setCompanyEmail] = useState(currentUser?.email || 'iletisim@sirket.com');
  const [companyAddress, setCompanyAddress] = useState('Maslak Mah. Büyükdere Cad. No: 12');
  const [companyCity, setCompanyCity] = useState('İstanbul / Türkiye');

  // STEP 5 & 6: Generation & Live Preview
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState('Tasarım parametreleri hazırlanıyor...');
  const [generatedPlan, setGeneratedPlan] = useState<AISitePlan | null>(null);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  // Auto-slugify subdomain from site name
  const handleSiteNameChange = (val: string) => {
    setSiteName(val);
    if (!subdomain || subdomain === (siteName || '').toLowerCase().replace(/[^a-z0-9]/g, '')) {
      const slug = (val || '')
        .toLowerCase()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]/g, '');
      setSubdomain(slug);
    }
  };

  const samplePrompts = [
    {
      label: 'Göcek Lüks VIP Gulet & Motoryat Kiralama',
      industry: 'YACHT_RENTAL' as IndustryType,
      vibe: 'AEGEAN_COASTAL' as AIDesignVibe,
      prompt: 'Göcek, Fethiye ve Bodrum koylarında lüks mavi tur charter hizmeti sunan, şeffaf fiyatlı ve 7/24 VIP asistan destekli yat kiralama acentesi.',
    },
    {
      label: 'B2B Kurumsal Danışmanlık & Finans',
      industry: 'CORPORATE' as IndustryType,
      vibe: 'CORPORATE_PRESTIGE' as AIDesignVibe,
      prompt: 'Orta ve büyük ölçekli şirketlere kurumsal finansman, risk yönetimi ve stratejik yönetim danışmanlığı sağlayan prestijli B2B danışmanlık firması.',
    },
    {
      label: 'Bodrum Lüks Katamaran Filosu',
      industry: 'YACHT_RENTAL' as IndustryType,
      vibe: 'LUXURY_DARK' as AIDesignVibe,
      prompt: 'Bodrum çıkışlı haftalık özel şefli katamaran turları, modern rota haritası ve hızlı rezervasyon talep formu içeren premium charter web sitesi.',
    },
    {
      label: 'Teknoloji & SaaS Yazılım Şirketi',
      industry: 'CORPORATE' as IndustryType,
      vibe: 'TECH_VIBRANT' as AIDesignVibe,
      prompt: 'KOBİ’ler için bulut tabanlı ERP, yapay zeka otomasyonları ve kurumsal mobil yazılım çözümleri üreten yenilikçi yazılım şirketi.',
    },
  ];

  const handleStartGeneration = async () => {
    setCurrentStep(5);
    setIsGenerating(true);
    setGenerationProgress(15);
    setGenerationStatus('Yapay zeka sektörel gereksinimleri analiz ediyor...');
    setErrorMsg(null);

    const profile: AIBusinessProfile = {
      prompt: userPrompt || `${siteName} için profesyonel web sitesi.`,
      companyName: companyName || siteName || 'Adiosoft Müşterisi',
      industry,
      location: `${companyCity} - ${companyAddress}`,
      phone: companyPhone,
      email: companyEmail,
      vibe: designVibe,
      targetAudience: industry === 'YACHT_RENTAL' ? 'Lüks Turizm & Yat Kiralama Müşterileri' : 'Kurumsal Müşteriler',
      keyServicesOrFleet: selectedHighlights,
    };

    try {
      const plan = await aiClientService.generateSitePlan(profile, (prog) => {
        setGenerationProgress(prog.progressPercent);
        setGenerationStatus(prog.message);
      });

      const isYacht = industry === 'YACHT_RENTAL';
      const requiredCount = isYacht ? 7 : 6;
      if (!plan.pages || plan.pages.length < requiredCount) {
        const struct = SiteStructureService.buildStructure({
          siteId: 'temp_site',
          siteName: siteName || companyName || 'Adiosoft Müşterisi',
          slug: subdomain || 'site',
          industry,
          colorPalette: plan.colorPalette,
          typography: plan.typography,
          contactInfo: {
            phone: companyPhone,
            email: companyEmail,
            address: companyAddress ? `${companyAddress}, ${companyCity}` : companyCity,
          },
          userPrompt,
        });
        plan.pages = struct.pages;
        if (!plan.settings) plan.settings = {} as any;
        plan.settings.headerMenu = struct.headerMenu;
      }

      setGeneratedPlan(plan);
      setGenerationProgress(100);
      setIsGenerating(false);
      setCurrentStep(6); // Step 6: Live Preview & Review

      confetti({
        particleCount: 70,
        spread: 60,
        origin: { y: 0.6 },
      });
    } catch (err: any) {
      console.error('AI Generation failed, falling back:', err);
      // Construct complete client-side structure using SiteStructureService (no blank canvas)
      const struct = SiteStructureService.buildStructure({
        siteId: 'temp_site',
        siteName: siteName || companyName || 'Adiosoft Web Sitesi',
        slug: subdomain || 'site',
        industry,
        colorPalette: {
          primaryColor: industry === 'YACHT_RENTAL' ? '#0369A1' : '#1E40AF',
          secondaryColor: industry === 'YACHT_RENTAL' ? '#0C4A6E' : '#0F172A',
          accentColor: industry === 'YACHT_RENTAL' ? '#F59E0B' : '#10B981',
          backgroundColor: '#FFFFFF',
          textColor: '#0F172A',
        },
        typography: {
          fontFamily: 'Inter',
          headingFont: 'Plus Jakarta Sans',
        },
        contactInfo: {
          phone: companyPhone,
          email: companyEmail,
          address: companyAddress ? `${companyAddress}, ${companyCity}` : companyCity,
        },
        userPrompt,
      });

      const fallbackPlan: AISitePlan = {
        name: siteName || companyName || 'Adiosoft Web Sitesi',
        slug: subdomain || 'site',
        industry,
        vibe: designVibe,
        colorPalette: {
          primaryColor: industry === 'YACHT_RENTAL' ? '#0369A1' : '#1E40AF',
          secondaryColor: industry === 'YACHT_RENTAL' ? '#0C4A6E' : '#0F172A',
          accentColor: industry === 'YACHT_RENTAL' ? '#F59E0B' : '#10B981',
          backgroundColor: '#FFFFFF',
          textColor: '#0F172A',
        },
        typography: {
          fontFamily: 'Inter',
          headingFont: 'Plus Jakarta Sans',
        },
        settings: {
          siteName: siteName || companyName,
          logoText: companyName || siteName,
          tagline: `${companyName || siteName} - Yeni Nesil Çözümler`,
          phone: companyPhone || '',
          email: companyEmail || '',
          address: companyAddress ? `${companyAddress}, ${companyCity}` : companyCity || '',
          headerMenu: struct.headerMenu,
          headerButtonText: industry === 'YACHT_RENTAL' ? 'Rezervasyon Yap' : 'İletişime Geçin',
          headerButtonAction: {
            type: 'page',
            targetPage: industry === 'YACHT_RENTAL' ? '/rezervasyon' : '/iletisim',
          },
        },
        seo: {
          title: `${siteName || 'Adiosoft'} - Resmi Web Sitesi`,
          description: userPrompt || 'Kurumsal hizmetler ve profesyonel çözümler.',
          keywords: ['web sitesi', 'kurumsal', (industry || '').toLowerCase()].filter(Boolean),
        },
        pages: struct.pages,
        yachts: [],
        blogPosts: [],
      };

      setGeneratedPlan(fallbackPlan);
      setIsGenerating(false);
      setCurrentStep(6);
    }
  };

  const handleFinalizeAndOpenEditor = async () => {
    if (!generatedPlan) return;
    setIsSaving(true);
    try {
      // Ensure the generated site contains user's actual contact information
      const planWithRealData: AISitePlan = {
        ...generatedPlan,
        name: siteName || generatedPlan.name,
      };

      const newSite = await createSiteWithAI(planWithRealData);
      onSiteCreated(newSite.id);
      onClose();
    } catch (err: any) {
      console.error('Failed to create site with AI:', err);
      // Fallback create standard site
      try {
        const standardSite = await createNewSite(
          siteName || 'Yeni Web Sitesi',
          subdomain || 'site',
          industry,
          industry === 'YACHT_RENTAL' ? 'tpl_yacht_luxury' : 'tpl_corp_modern',
          industry === 'YACHT_RENTAL' ? 'PLAN_YACHT_ANNUAL' : 'PLAN_CORP_ANNUAL'
        );
        onSiteCreated(standardSite.id);
        onClose();
      } catch (e: any) {
        setErrorMsg('Site oluşturulamadı: ' + e.message);
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-md p-3 sm:p-4 overflow-y-auto animate-in fade-in duration-150">
      <div className="relative w-full max-w-4xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col my-auto max-h-[95vh]">
        {/* Modal Top Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-500/20">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-wide">
                Adiosoft AI Website Generator
              </h2>
              <span className="text-[11px] text-slate-400">
                Adım {currentStep} / 7: {
                  currentStep === 1 ? 'Web Sitesi Adı' :
                  currentStep === 2 ? 'Sektör Seçimi' :
                  currentStep === 3 ? 'Nasıl Bir Web Sitesi İstiyorsunuz?' :
                  currentStep === 4 ? 'Gerçek İşletme Bilgileri' :
                  currentStep === 5 ? 'AI Tasarım Üretimi' :
                  currentStep === 6 ? 'Canlı Önizleme & Onay' : 'Editöre Geçiş'
                }
              </span>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {errorMsg && (
            <div className="mb-4 p-3 bg-rose-500/15 border border-rose-500/30 rounded-xl text-rose-300 text-xs">
              {errorMsg}
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 1: WEBSITE ADI */}
          {/* ============================================================ */}
          {currentStep === 1 && (
            <div className="space-y-6 max-w-xl mx-auto py-4">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 rounded-2xl bg-blue-500/15 border border-blue-500/30 text-blue-400 flex items-center justify-center mx-auto">
                  <Globe className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-black text-white">Web Sitenizin Adı Nedir?</h3>
                <p className="text-xs text-slate-400">
                  Şirketinizin, markanızın veya projenizin ismini girin.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Web Sitesi / Marka Adı
                  </label>
                  <input
                    type="text"
                    required
                    value={siteName}
                    onChange={(e) => handleSiteNameChange(e.target.value)}
                    placeholder="Örn: Aegean Azure Yachting veya Vanguard Danışmanlık"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-hidden focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Ücretsiz Web Adresiniz (URL Slug)
                  </label>
                  <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl px-3 text-sm text-slate-300 focus-within:border-blue-500">
                    <span className="text-slate-500 text-xs font-mono select-none mr-1">
                      adiosoft.com/
                    </span>
                    <input
                      type="text"
                      required
                      value={subdomain}
                      onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      className="bg-transparent py-2.5 flex-1 text-white focus:outline-hidden text-xs sm:text-sm font-mono"
                      placeholder="site-adi"
                    />
                  </div>
                  <span className="text-[10px] text-slate-500 mt-1 block">
                    * Daha sonra kendi özel domain adresinizi (ornek.com) bağlayabilirsiniz.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 2: SEKTÖR SEÇİMİ */}
          {/* ============================================================ */}
          {currentStep === 2 && (
            <div className="space-y-6 max-w-2xl mx-auto py-4">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 text-indigo-400 flex items-center justify-center mx-auto">
                  <Building2 className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-black text-white">Faaliyet Gösterdiğiniz Sektör</h3>
                <p className="text-xs text-slate-400">
                  Yapay zeka sektörel mimariyi ve gerekli bileşenleri otomatik ayarlayacaktır.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Kurumsal */}
                <div
                  onClick={() => {
                    if (sectorQuotas.CORPORATE.allowed && sectorQuotas.CORPORATE.remaining > 0) {
                      setIndustry('CORPORATE');
                    }
                  }}
                  className={`p-5 rounded-2xl border transition flex flex-col justify-between ${
                    industry === 'CORPORATE'
                      ? 'bg-blue-600/15 border-blue-500 ring-2 ring-blue-500/30'
                      : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                  } ${
                    !sectorQuotas.CORPORATE.allowed || sectorQuotas.CORPORATE.remaining <= 0
                      ? 'opacity-60 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center">
                        <Building2 className="w-5 h-5" />
                      </div>
                      {industry === 'CORPORATE' && (
                        <CheckCircle2 className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-white text-base">Kurumsal Web Sitesi</h4>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          sectorQuotas.CORPORATE.remaining > 0
                            ? 'bg-emerald-500/20 text-emerald-300'
                            : 'bg-amber-500/20 text-amber-300'
                        }`}>
                          {sectorQuotas.CORPORATE.allowed
                            ? `${sectorQuotas.CORPORATE.used}/${sectorQuotas.CORPORATE.total} Kullanıldı`
                            : 'Paketsiz'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Şirketler, holdingler, danışmanlık firmaları ve KOBİ'ler için kurumsal vitrin.
                      </p>
                    </div>
                    <div className="text-xs text-slate-400 space-y-1 pt-1">
                      <div>✓ Kurumsal Blog & Haberler</div>
                      <div>✓ Hizmetler Kataloğu</div>
                      <div>✓ İletişim & Teklif Formu</div>
                    </div>
                  </div>
                  <div className="border-t border-slate-800 pt-3 mt-4 flex items-center justify-between text-xs">
                    <span className="font-semibold text-blue-400">Paket: 15.000 TL / YIL</span>
                    <span className={`text-[11px] font-bold ${
                      sectorQuotas.CORPORATE.remaining > 0 ? 'text-emerald-400' : 'text-amber-400'
                    }`}>
                      {sectorQuotas.CORPORATE.remaining > 0
                        ? `${sectorQuotas.CORPORATE.remaining} Hak Kalan`
                        : 'Hak Doldu'}
                    </span>
                  </div>
                </div>

                {/* Yat Kiralama */}
                <div
                  onClick={() => {
                    if (sectorQuotas.YACHT_RENTAL.allowed && sectorQuotas.YACHT_RENTAL.remaining > 0) {
                      setIndustry('YACHT_RENTAL');
                    }
                  }}
                  className={`p-5 rounded-2xl border transition flex flex-col justify-between ${
                    industry === 'YACHT_RENTAL'
                      ? 'bg-cyan-600/15 border-cyan-500 ring-2 ring-cyan-500/30'
                      : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                  } ${
                    !sectorQuotas.YACHT_RENTAL.allowed || sectorQuotas.YACHT_RENTAL.remaining <= 0
                      ? 'opacity-60 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
                        <Ship className="w-5 h-5" />
                      </div>
                      {industry === 'YACHT_RENTAL' && (
                        <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-white text-base">Lüks Yat Kiralama & Filo</h4>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          sectorQuotas.YACHT_RENTAL.remaining > 0
                            ? 'bg-cyan-500/20 text-cyan-300'
                            : 'bg-amber-500/20 text-amber-300'
                        }`}>
                          {sectorQuotas.YACHT_RENTAL.allowed
                            ? `${sectorQuotas.YACHT_RENTAL.used}/${sectorQuotas.YACHT_RENTAL.total} Kullanıldı`
                            : 'Paketsiz'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Yat acenteleri, tekne sahipleri ve mavi tur operatörleri için özel SaaS.
                      </p>
                    </div>
                    <div className="text-xs text-slate-400 space-y-1 pt-1">
                      <div>✓ Yat Filosu Yönetimi</div>
                      <div>✓ Müsaitlik & Doluluk Takvimi</div>
                      <div>✓ Online Rezervasyon Motoru</div>
                    </div>
                  </div>
                  <div className="border-t border-slate-800 pt-3 mt-4 flex items-center justify-between text-xs">
                    <span className="font-semibold text-cyan-400">Paket: 36.000 TL / YIL</span>
                    <span className={`text-[11px] font-bold ${
                      sectorQuotas.YACHT_RENTAL.remaining > 0 ? 'text-cyan-400' : 'text-amber-400'
                    }`}>
                      {sectorQuotas.YACHT_RENTAL.remaining > 0
                        ? `${sectorQuotas.YACHT_RENTAL.remaining} Hak Kalan`
                        : 'Hak Doldu'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 3: NASIL BİR WEB SİTESİ İSTİYORSUNUZ? (NATURAL LANGUAGE AI PROMPT) */}
          {/* ============================================================ */}
          {currentStep === 3 && (
            <div className="space-y-5 max-w-2xl mx-auto py-2">
              <div className="text-center space-y-1.5">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white flex items-center justify-center mx-auto shadow-md shadow-blue-500/20">
                  <Sparkles className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-black text-white">Nasıl Bir Web Sitesi İstiyorsunuz?</h3>
                <p className="text-xs text-slate-400">
                  Kendi cümlelerinizle web sitenizin tarzını, amacını ve öne çıkarmak istediğiniz özellikleri anlatın.
                </p>
              </div>

              {/* Natural language prompt textarea */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-300">
                    Doğal Dil Yapay Zeka İstek Alanı
                  </label>
                  <span className="text-[11px] text-slate-500">Serbestçe yazabilirsiniz</span>
                </div>
                <textarea
                  rows={4}
                  required
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  placeholder="Örn: Göcek ve Fethiye koylarında 6 adet lüks motoryat ile VIP mavi tur hizmeti veren acenteyiz. Sitede WhatsApp doğrudan rezervasyon butonu, şık koy fotoğrafları, teknik şartname alanı ve modern bir iletişim formu olsun..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-xs sm:text-sm text-white focus:outline-hidden focus:border-blue-500 transition shadow-inner leading-relaxed"
                />
              </div>

              {/* Quick Sample Prompts */}
              <div className="space-y-2">
                <span className="text-[11px] font-semibold text-slate-400 block">
                  Örnek İstek Şablonları (Tek tıkla uygulayın):
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {samplePrompts
                    .filter((s) => s.industry === industry)
                    .map((sample, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setUserPrompt(sample.prompt);
                          setDesignVibe(sample.vibe);
                        }}
                        className="p-2.5 rounded-xl bg-slate-950 hover:bg-slate-800/80 border border-slate-800 text-left text-xs transition group"
                      >
                        <span className="font-semibold text-slate-200 group-hover:text-blue-400 block">
                          {sample.label}
                        </span>
                        <span className="text-[10px] text-slate-500 line-clamp-1 mt-0.5">
                          {sample.prompt}
                        </span>
                      </button>
                    ))}
                </div>
              </div>

              {/* Tone of Voice / Vibe */}
              <div className="space-y-2 pt-1">
                <span className="text-[11px] font-semibold text-slate-400 block">
                  Tasarım Ses Tonu & Atmosferi:
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  {[
                    { id: 'CORPORATE_PRESTIGE' as AIDesignVibe, label: 'Prestijli & Kurumsal' },
                    { id: 'AEGEAN_COASTAL' as AIDesignVibe, label: 'Ege & Lüks Sahil' },
                    { id: 'LUXURY_DARK' as AIDesignVibe, label: 'Koyu Lüks & VIP' },
                    { id: 'TECH_VIBRANT' as AIDesignVibe, label: 'Dinamik & Teknoloji' },
                  ].map((vibe) => (
                    <button
                      key={vibe.id}
                      type="button"
                      onClick={() => setDesignVibe(vibe.id)}
                      className={`p-2.5 rounded-xl border text-center transition font-medium ${
                        designVibe === vibe.id
                          ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                          : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                      }`}
                    >
                      {vibe.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 4: GERÇEK İŞLETME BİLGİLERİ */}
          {/* ============================================================ */}
          {currentStep === 4 && (
            <div className="space-y-5 max-w-xl mx-auto py-2">
              <div className="text-center space-y-1.5">
                <div className="w-11 h-11 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-black text-white">Gerçek İşletme Bilgileri</h3>
                <p className="text-xs text-slate-400">
                  Adiosoft sahte/demo telefon veya firma adı üretmez. Web sitenizde yer alacak gerçek iletişim verilerinizi girin.
                </p>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Resmi Firma / Marka Ünvanı
                  </label>
                  <input
                    type="text"
                    required
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={siteName || 'Örn: Azure Turizm Yatçılık Tic. Ltd. Şti.'}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-300 block mb-1">
                      Telefon Numarası
                    </label>
                    <input
                      type="tel"
                      required
                      value={companyPhone}
                      onChange={(e) => setCompanyPhone(e.target.value)}
                      placeholder="+90 (532) XXX XX XX"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-hidden focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-slate-300 block mb-1">
                      İletişim E-Postası
                    </label>
                    <input
                      type="email"
                      required
                      value={companyEmail}
                      onChange={(e) => setCompanyEmail(e.target.value)}
                      placeholder="info@sirketiniz.com"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-hidden focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-300 block mb-1">
                      Şehir / Bölge
                    </label>
                    <input
                      type="text"
                      required
                      value={companyCity}
                      onChange={(e) => setCompanyCity(e.target.value)}
                      placeholder="Örn: Göcek / Muğla"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-hidden focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-slate-300 block mb-1">
                      Açık Adres / Marina
                    </label>
                    <input
                      type="text"
                      required
                      value={companyAddress}
                      onChange={(e) => setCompanyAddress(e.target.value)}
                      placeholder="Örn: D-Marin Göcek No: 14"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-hidden focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 5: AI GENERATION PROGRESS */}
          {/* ============================================================ */}
          {currentStep === 5 && (
            <div className="py-12 px-4 max-w-md mx-auto text-center space-y-6">
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
                <div className="w-full h-full rounded-full flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-blue-400 animate-pulse" />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white">Yapay Zeka Web Sitenizi Tasarlıyor</h3>
                <p className="text-xs text-slate-400">{generationStatus}</p>
              </div>

              <div className="space-y-1.5">
                <div className="w-full bg-slate-950 rounded-full h-2.5 border border-slate-800 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-600 to-indigo-500 h-full transition-all duration-300 rounded-full"
                    style={{ width: `${generationProgress}%` }}
                  />
                </div>
                <div className="flex justify-between text-[11px] text-slate-500">
                  <span>Tasarım İskeleti & Tokenlar</span>
                  <span className="font-bold text-blue-400">%{generationProgress}</span>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 6 & 7: AI CANLI ÖNİZLEME & ONAY */}
          {/* ============================================================ */}
          {currentStep === 6 && generatedPlan && (
            <div className="space-y-5">
              {/* Preview Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-950 p-3 rounded-xl border border-slate-800">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-white">{generatedPlan.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    AI Tasarım Taslağı
                  </span>
                </div>

                <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-lg p-1">
                  <button
                    onClick={() => setPreviewDevice('desktop')}
                    className={`p-1.5 rounded transition ${
                      previewDevice === 'desktop' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                    title="Masaüstü Önizleme"
                  >
                    <Monitor className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setPreviewDevice('tablet')}
                    className={`p-1.5 rounded transition ${
                      previewDevice === 'tablet' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                    title="Tablet Önizleme"
                  >
                    <Tablet className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setPreviewDevice('mobile')}
                    className={`p-1.5 rounded transition ${
                      previewDevice === 'mobile' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                    title="Mobil Önizleme"
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Simulated Website Canvas Frame */}
              <div className="flex justify-center bg-slate-950 rounded-2xl p-4 border border-slate-800 min-h-[380px] max-h-[460px] overflow-y-auto custom-scrollbar">
                <div
                  className={`bg-white text-slate-900 rounded-xl shadow-xl transition-all duration-300 overflow-hidden flex flex-col ${
                    previewDevice === 'desktop'
                      ? 'w-full max-w-3xl'
                      : previewDevice === 'tablet'
                      ? 'w-[520px]'
                      : 'w-[320px]'
                  }`}
                >
                  {/* Website Header */}
                  <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white/95">
                    <span className="font-extrabold text-sm tracking-tight text-slate-900">
                      {companyName || siteName}
                    </span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-medium text-slate-600 hidden sm:inline">Ana Sayfa</span>
                      <span className="font-medium text-slate-600 hidden sm:inline">Hakkımızda</span>
                      <span className="font-medium text-slate-600 hidden sm:inline">Hizmetler</span>
                      <span className="px-3 py-1.5 bg-blue-600 text-white rounded-lg font-bold text-[11px]">
                        {industry === 'YACHT_RENTAL' ? 'Rezervasyon' : 'İletişim & Teklif'}
                      </span>
                    </div>
                  </div>

                  {/* Hero Section */}
                  <div className="px-6 py-12 bg-gradient-to-b from-slate-50 to-white text-center space-y-3">
                    <h1 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight">
                      {generatedPlan.settings?.tagline || (generatedPlan.pages?.[0]?.sections?.find(s => s.type === 'HERO')?.content?.title) || `${companyName || siteName}`}
                    </h1>
                    <p className="text-xs text-slate-600 max-w-md mx-auto leading-relaxed">
                      {generatedPlan.pages?.[0]?.sections?.find(s => s.type === 'HERO')?.content?.subtitle || 'Müşterilerimize özel, yüksek standartlarda kurumsal hizmetler sunuyoruz.'}
                    </p>
                    <div className="pt-2 flex justify-center gap-2">
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20">
                        Hemen Keşfedin
                      </button>
                      <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold">
                        Bize Ulaşın
                      </button>
                    </div>
                  </div>

                  {/* Services / Fleet preview */}
                  <div className="px-6 py-8 border-t border-slate-100 bg-white">
                    <div className="text-center mb-6">
                      <span className="text-[10px] uppercase font-bold text-blue-600 tracking-wider">
                        {industry === 'YACHT_RENTAL' ? 'Öne Çıkan Yatlar' : 'Hizmetlerimiz'}
                      </span>
                      <h2 className="text-base font-bold text-slate-900">
                        {industry === 'YACHT_RENTAL' ? 'Lüks Mavi Tur Deneyimi' : 'Kurumsal Uzmanlık Alanlarımız'}
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="p-3.5 rounded-xl border border-slate-100 bg-slate-50/50 space-y-1.5">
                          <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">
                            {num}
                          </div>
                          <h4 className="font-bold text-slate-800 text-xs">
                            {industry === 'YACHT_RENTAL' ? `VIP Mavi Tur Rotası ${num}` : `Danışmanlık Hizmeti ${num}`}
                          </h4>
                          <p className="text-[11px] text-slate-500">
                            Yüksek müşteri memnuniyeti odaklı profesyonel hizmet sunumu.
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Contact Footer Preview */}
                  <div className="px-6 py-6 bg-slate-900 text-white text-xs border-t border-slate-800 mt-auto flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <span className="font-bold block">{companyName || siteName}</span>
                      <span className="text-[10px] text-slate-400">
                        {companyPhone} • {companyEmail}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-500">
                      © {new Date().getFullYear()} Tüm Hakları Saklıdır.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Controls */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950 flex items-center justify-between">
          <div>
            {currentStep > 1 && currentStep !== 5 && (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep === 6 ? 3 : currentStep - 1)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-bold transition flex items-center gap-1.5"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Geri
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {currentStep < 4 && (
              <button
                type="button"
                disabled={
                  (currentStep === 1 && !siteName.trim()) ||
                  (currentStep === 2 && !canCreateSectorWebsite(industry))
                }
                onClick={() => setCurrentStep(currentStep + 1)}
                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-md shadow-blue-500/20"
              >
                Devam Et
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}

            {currentStep === 4 && (
              <button
                type="button"
                disabled={isGenerating}
                onClick={handleStartGeneration}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold transition flex items-center gap-2 shadow-lg shadow-blue-500/25"
              >
                <Sparkles className="w-4 h-4" />
                AI Tasarımı Oluştur
              </button>
            )}

            {currentStep === 6 && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(3); // Go back to prompt step to adjust
                  }}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold transition flex items-center gap-1.5"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  Tasarımı Yeniden Oluştur
                </button>

                <button
                  id="btn-use-design-open-editor"
                  type="button"
                  disabled={isSaving}
                  onClick={handleFinalizeAndOpenEditor}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold transition flex items-center gap-2 shadow-lg shadow-emerald-500/25"
                >
                  <Check className="w-4 h-4" />
                  {isSaving ? 'Editör Açılıyor...' : 'Bu Tasarımı Kullan & Editörde Aç'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISiteCreationFlow;
